/**
 * Contractor-side Client View management. Every call is a manager+ RPC
 * (see 20261009120000_client_view_sharing.sql); the database re-checks the
 * role, so the capability check here is only a fast, friendly refusal.
 */
import { supabase } from "../../supabase";
import { assertCan, CAPABILITY } from "../permissions";
import { isOffline } from "../connectivity";
import { shouldSkipSupabase } from "../guestMode";

export const OFFLINE_CLIENT_VIEW_MESSAGE = "Reconnect to manage Client View.";
const DENIED = "You do not have permission to manage Client View.";

function guard(actor, { write = true } = {}) {
  assertCan(actor, CAPABILITY.PROJECT_CLIENT_VIEW_MANAGE, {}, undefined, DENIED);
  if (shouldSkipSupabase(actor)) throw new Error("Demo Mode — Sign in to use Client View.");
  if (write && isOffline()) throw new Error(OFFLINE_CLIENT_VIEW_MESSAGE);
}

async function rpc(name, args) {
  const { data, error } = await supabase.rpc(name, args);
  if (error) throw error;
  return data;
}

/** The active share for the project, or null. */
export async function getProjectShare(actor, projectId) {
  guard(actor, { write: false });
  if (isOffline()) return null;
  return rpc("cp_project_share_get", { p_project_id: projectId });
}

export async function enableProjectShare(actor, projectId) {
  guard(actor);
  return rpc("cp_project_share_enable", { p_project_id: projectId });
}

export async function regenerateProjectShare(actor, projectId) {
  guard(actor);
  return rpc("cp_project_share_regenerate", { p_project_id: projectId });
}

export async function disableProjectShare(actor, projectId) {
  guard(actor);
  await rpc("cp_project_share_disable", { p_project_id: projectId });
}

export async function setShareDescription(actor, projectId, text) {
  guard(actor);
  return rpc("cp_project_share_set_description", { p_project_id: projectId, p_text: text });
}

/** Share issued project invoices with the client. Off unless set. */
export async function setShareBilling(actor, projectId, enabled) {
  guard(actor);
  return rpc("cp_project_share_set_billing", {
    p_project_id: projectId,
    p_enabled: Boolean(enabled),
  });
}

/** Publish / unpublish pictures. Returns how many changed. */
export async function setPicturesClientVisible(actor, projectId, pictureIds, visible) {
  guard(actor);
  const ids = [...new Set(pictureIds || [])];
  if (!ids.length) return 0;
  return rpc("cp_project_pictures_set_client_visible", {
    p_project_id: projectId,
    p_picture_ids: ids,
    p_visible: Boolean(visible),
  });
}

export async function listClientConversation(actor, projectId) {
  guard(actor, { write: false });
  if (isOffline()) return [];
  return (await rpc("cp_client_conversation_list", { p_project_id: projectId })) || [];
}

export async function replyToClient(actor, projectId, body, { pictureId = null } = {}) {
  guard(actor);
  return rpc("cp_client_conversation_reply", {
    p_project_id: projectId,
    p_body: body,
    p_sender_label: actor?.employee_name || null,
    p_picture_id: pictureId,
  });
}
